import React from "react";
import { motion } from "framer-motion";
import { PLAYERS } from "../lib/quattro";
import { getInitials, colorFromName } from "../lib/avatar";
import { isPresetAvatar } from "../lib/avatars_catalog";
import AvatarRender from "./AvatarRender";
import { CardBack } from "./CardBack";

const GLOW_CLASS = ["glow-yellow", "glow-blue", "glow-red", "glow-purple"];

export default function PlayerHUD({ players, decks, currentPlayer, drawnCard, humanId, activeSkins = {} }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full" data-testid="player-hud">
      {players.map((p) => {
        const active = currentPlayer === p.id;
        const deckCount = decks[p.id].length + (drawnCard && drawnCard.owner === p.id ? 1 : 0);
        const isHuman = humanId === p.id;
        // Network multiplayer marks each Player with .isBot=true/false; offline AI players
        // omit the field. Treat absent isBot as bot for offline games (the drawn-from-name
        // initials only apply when we have a real human-entered name).
        const isOnlineHuman = p.isBot === false;
        const showInitials = isHuman || isOnlineHuman;
        const hasPresetAvatar = showInitials && isPresetAvatar(p.avatar);
        const face = showInitials ? getInitials(p.label || p.name) : p.suit;
        // Personal accent color, derived from the player's name. For bots we keep the seat
        // color so the suit-symbol avatar reads cleanly.
        const ring = showInitials ? colorFromName(p.label || p.name) : p.color;
        return (
          <motion.div
            key={p.id}
            className={`player-pill ${active ? "player-active" : ""}`}
            animate={active ? { boxShadow: [`0 0 0px ${p.glow}`, `0 0 24px ${p.glow}`, `0 0 0px ${p.glow}`] } : {}}
            transition={{ duration: 1.6, repeat: Infinity }}
            style={{ borderColor: active ? p.color : undefined }}
            data-testid={`player-card-${p.id}`}
          >
            <div className="flex items-center gap-2 md:gap-3">
              {hasPresetAvatar ? (
                <div
                  className="flex-shrink-0"
                  style={{
                    width: 36, height: 36,
                    boxShadow: active ? `0 0 18px ${p.glow}` : undefined,
                    borderRadius: "50%", overflow: "hidden",
                  }}
                  data-testid={`player-avatar-${p.id}`}
                >
                  <AvatarRender avatar={p.avatar} name={p.label || p.name} size={36} />
                </div>
              ) : (
                <div
                  className="rounded-full flex items-center justify-center font-display font-black flex-shrink-0"
                  style={{
                    width: 36, height: 36,
                    background: `linear-gradient(155deg, ${p.color}, #111)`,
                    color: p.id === 0 ? "#0B0C10" : "#fff",
                    boxShadow: active ? `0 0 18px ${p.glow}` : undefined,
                    fontSize: showInitials ? 13 : 17,
                    letterSpacing: showInitials ? 0.5 : 0,
                    border: `2px solid ${ring}`,
                  }}
                  data-testid={`player-avatar-${p.id}`}
                >
                  {face}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold truncate" style={{ fontSize: 12 }} data-testid={`player-name-${p.id}`}>
                  {p.label}
                </div>
                <div className="label-eyebrow truncate" style={{ fontSize: 9 }}>
                  {isHuman ? "YOU" : (p.isBot === true ? "BOT" : (p.level != null ? `LV ${p.level}` : p.name))}
                  {p.isBot === false && p.chips != null && (
                    <span style={{ marginLeft: 6, color: "#FFD700" }}>· ◆{p.chips.toLocaleString()}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-1.5">
              <span className="label-eyebrow flex-shrink-0" style={{ fontSize: 9 }}>Deck</span>
              <div
                className="flex-shrink-0 rounded-md overflow-hidden"
                style={{ width: 28, height: 40 }}
                title={`Card back: ${activeSkins[p.id] || "default"}`}
                data-testid={`deck-back-${p.id}`}
              >
                <CardBack
                  skin={activeSkins[p.id] || "default"}
                  color={p.color}
                  suit={p.suit}
                  size="sm"
                />
              </div>
              <span className="font-mono font-bold flex-1 text-right" data-testid={`deck-${p.id}`} style={{ color: p.color, fontSize: 13 }}>
                {deckCount}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
